import React from "react";
import { Box, Stack, Typography, Avatar } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useCachedAvatar } from "../../hooks/useCachedAvatar";
import { getFirstSentence, getBodyText, normalizeTextAttachment } from "../../utils/textUtils";

/**
 * CoverStoryCard
 * @param {object} props
 * @param {object} props.thread - 스레드 메타 (id, text, permalink, timestamp, likes, replies 등)
 * @param {object} [props.user] - 사용자 메타 (expects id or profile.id)
 * @param {boolean} [props.isMobile]
 */
const CoverStoryCard = ({ thread, user, isMobile }) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const username = user?.profile?.username || user?.profile?.name || "";
	const userId = String(user?.id || user?.user_id || user?.profile?.id || "");
	const displayName = user?.profile?.name || "";
	const fallbackAvatar =
		user?.cachedAvatarURL || user?.profile?.threads_profile_picture_url || null;
	const { imageSrc } = useCachedAvatar(userId, fallbackAvatar, 160);
	const title = getFirstSentence(thread?.text || "") || `Thread ${thread?.id}`;
	const bodyText = getBodyText(thread?.text || "").trim();
	const previewBody = bodyText || normalizeTextAttachment(thread?.text_attachment).trim();

	return (
		<Stack
			spacing={isMobile ? 2 : 24}
			sx={{
				width: "100%",
				height: "100%",
				py: isMobile ? 3 : 5,
				px: isMobile ? 2 : 5,
				transition: "background-color 0.2s ease-in-out, color 0.2s ease-in-out",
				cursor: "pointer",
				"&:hover": {
					backgroundColor: theme.palette.primary.main,
					"& *": {
						color: theme.palette.background.paper + " !important",
					},
				},
			}}
			onClick={() => {
				// 1. collectionId가 있으면 컬렉션 페이지로 이동
				const colId = thread?.collectionId;
				if (colId && thread?.id) {
					navigate(`/collection/${colId}?threadID=${thread.id}`);
					return;
				}
				// 2. permalink가 있으면 Threads.net으로 이동
				if (thread?.permalink) {
					window.open(thread.permalink, '_blank', 'noopener,noreferrer');
				}
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				<Box
					sx={{ textDecoration: "none" }}
				>
					<Typography
						variant={isMobile ? "body1" : "h5"}
						sx={{
							fontFamily: '"RIDIBatang", "Noto Serif KR", serif',
							fontWeight: 700,
							color: theme.palette.text.primary,
							lineHeight: isMobile ? 1.4 : 1.5,
							height: isMobile ? "auto" : "72px",
							overflow: "hidden",
							wordBreak: "keep-all",
							textOverflow: "ellipsis",
							mb: isMobile ? 2 : 3,
							fontSize: isMobile ? "1.1rem" : undefined,
							display: isMobile ? "-webkit-box" : "block",
							WebkitLineClamp: isMobile ? 2 : undefined,
							WebkitBoxOrient: isMobile ? "vertical" : undefined,
						}}
					>
						{title.replace(/�/g, "")}
					</Typography>
					{/* 항상 본문 영역 렌더링 (빈 경우에도 높이 유지하여 인접 카드와 정렬) */}
					<Typography
						variant={isMobile ? "caption" : "body1"}
						sx={{
							color: previewBody
								? (isMobile ? 'transparent' : theme.palette.text.primary)
								: 'transparent',
							display: "-webkit-box",
							WebkitLineClamp: isMobile ? 2 : 3,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
							fontSize: isMobile ? "0.875rem" : undefined,
							lineHeight: 1.5,
							height: isMobile ? '2.625rem' : '5rem',
							minHeight: isMobile ? '2.625rem' : '4.5rem',
						}}
					>
						{previewBody ? previewBody.replace(/�/g, "") : '\u00A0'}
					</Typography>
				</Box>
			</Box>
			<Stack
				direction="row"
				spacing={isMobile ? 1 : 1.5}
				alignItems="center"
				sx={{ minWidth: 0 }}
			>
				<Avatar
					src={imageSrc || undefined}
					sx={{ width: isMobile ? 24 : 44, height: isMobile ? 24 : 44 }}
				>
					{username ? username.charAt(0).toUpperCase() : "?"}
				</Avatar>
				<Stack direction="column" spacing={0} sx={{ minWidth: 0, overflow: "hidden" }}>
					<Typography
						variant={isMobile ? "caption" : "body1"}
						sx={{
							fontWeight: 700,
							fontSize: isMobile ? "0.75rem" : undefined,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							maxWidth: "100%",
						}}
					>
						{displayName}
					</Typography>
					<Typography
						variant={isMobile ? "caption" : "body2"}
						sx={{
							fontSize: isMobile ? "0.7rem" : "0.875rem",
							color: theme.palette.text.secondary,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							maxWidth: "100%",
						}}
					>
						@{username}
					</Typography>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default CoverStoryCard;
